import { useCallback, useRef } from "react";

import { WalletService } from "@/modules/dashboard/cards/services/walletsService";
import type { WalletTransactionEntity } from "@/modules/dashboard/cards/types/wallets-api.types";

import type { AdjustDirection, AdjustInlineProblem, AdjustOutcome } from "../types/cardWallets.types";
import { unresolvedAdjustAttempts } from "../utils/adjustAttempt";
import {
  ADJUST_AMOUNT_DECIMALS,
  adjustProviderTransactionId,
  buildAdjustPayload,
  normalizeAdjustResponse,
} from "../utils/cardWalletAdjust";
import { classifyAdjustError, staysOnReview } from "../utils/cardWalletAdjustErrors";

export interface AdjustSubmitInput {
  walletId: string;
  magnitude: string;
  direction: AdjustDirection;
  reference: string;
}

/** Either the attempt settled into an outcome, or it never left the review step. */
export type AdjustSubmitResult = { settled: AdjustOutcome } | { inline: AdjustInlineProblem };

/** What the lookup of an unconfirmed attempt found. */
export type AdjustLookupResult =
  | { kind: "found"; movement: WalletTransactionEntity }
  | { kind: "not_placed" }
  | { kind: "unknown" };

/** Newest first, so a movement created seconds ago is always inside this page. */
const LOOKUP_LIMIT = 200;

function decimalFromMetadata(value: unknown): string | null {
  if (typeof value === "number" && Number.isFinite(value)) return value.toFixed(ADJUST_AMOUNT_DECIMALS);
  if (typeof value === "string" && value.trim() !== "") return value;
  return null;
}

/**
 * The POST, its idempotency key and the record of an attempt whose answer never arrived.
 *
 * One `Idempotency-Key` per attempt: a retry after a lost answer replays the first adjust instead of moving
 * money twice, and `resetAttempt` — called on every form edit — mints a new one, since the backend refuses a
 * known key with a different body.
 *
 * There is no 2FA here. The endpoint declares none (unlike every comparable card-money route), so the review
 * step's acknowledgement is the only gate, and that is deliberate rather than an omission.
 */
export function useCardWalletAdjustment() {
  const keyRef = useRef<string | null>(null);

  const resetAttempt = useCallback(() => {
    keyRef.current = null;
  }, []);

  const submit = useCallback(async (input: AdjustSubmitInput): Promise<AdjustSubmitResult> => {
    const payload = buildAdjustPayload(input.magnitude, input.direction, input.reference);
    if (!payload) return { inline: { kind: "amount_zero" } };

    keyRef.current ??= crypto.randomUUID();

    // Written BEFORE the request leaves: if the answer is lost, a reload still knows what to look up.
    unresolvedAdjustAttempts.set(input.walletId, {
      reference: payload.external_reference,
      direction: input.direction,
      magnitude: input.magnitude,
    });

    try {
      const res = await WalletService.adjustWalletBalance(input.walletId, payload, keyRef.current);
      const normalized = normalizeAdjustResponse(res.data);
      unresolvedAdjustAttempts.set(input.walletId, null);
      keyRef.current = null;
      return {
        settled: {
          kind: "done",
          previousBalance: normalized.previousBalance,
          newBalance: normalized.newBalance,
          reference: payload.external_reference,
        },
      };
    } catch (error: unknown) {
      const classified = classifyAdjustError(error);

      if (classified.kind === "unconfirmed") {
        // The attempt stays on record — this is the one outcome nobody can act on without checking first.
        return { settled: { kind: "unconfirmed", reference: payload.external_reference } };
      }

      unresolvedAdjustAttempts.set(input.walletId, null);

      if (staysOnReview(classified.kind)) {
        return {
          inline: { kind: "rate_limited", retryAfterSeconds: classified.retryAfterSeconds },
        };
      }

      switch (classified.kind) {
        case "amount_zero":
          return { inline: { kind: "amount_zero" } };
        case "insufficient_funds":
          return { inline: { kind: "insufficient_funds" } };
        case "duplicate_reference":
          return { settled: { kind: "duplicate_reference", reference: payload.external_reference } };
        case "ineligible_wallet":
          return { settled: { kind: "ineligible_wallet" } };
        case "currency_mismatch":
          return { settled: { kind: "currency_mismatch" } };
        case "wallet_not_found":
          return { settled: { kind: "wallet_not_found" } };
        case "no_permission":
          return { settled: { kind: "no_permission" } };
        default:
          return { settled: { kind: "generic", message: classified.message } };
      }
    }
  }, []);

  /**
   * Settles an unconfirmed attempt with no extra endpoint: the reference is recoverable from the history,
   * because the movement's `provider_transaction_id` is derived from it.
   */
  const check = useCallback(async (walletId: string, reference: string): Promise<AdjustLookupResult> => {
    const target = adjustProviderTransactionId(walletId, reference);
    try {
      const res = await WalletService.getWalletTransactions(walletId, { limit: LOOKUP_LIMIT, skip: 0 });
      const movement = (res.data.data ?? []).find((row) => row.provider_transaction_id === target);
      if (movement) {
        unresolvedAdjustAttempts.set(walletId, null);
        keyRef.current = null;
        return { kind: "found", movement };
      }
      unresolvedAdjustAttempts.set(walletId, null);
      return { kind: "not_placed" };
    } catch {
      // The check itself failed: the attempt is still unresolved, so it must stay on record.
      return { kind: "unknown" };
    }
  }, []);

  return { submit, resetAttempt, check };
}

/** The before/after a looked-up movement carries, so a resumed attempt renders the same receipt. */
export function balancesFromMovement(movement: WalletTransactionEntity): {
  previousBalance: string | null;
  newBalance: string | null;
} {
  return {
    previousBalance: decimalFromMetadata(movement.metadata?.previous_balance),
    newBalance: decimalFromMetadata(movement.metadata?.new_balance),
  };
}
